import createHttp from '@/config/http';
import type { BulkOrderRequest } from '@/types/bulk-order';
import type {
	ConsignmentOrder,
	ConsignmentOrdersResponse,
} from '@/types/consignmentOrders';
import type { ProductSku } from '@/types/product';
import type { ResponseMetaData } from '@/types/service';

// localhost:3002 (Orders microservice port)
const http = createHttp(import.meta.env.VITE_ORDERS_URL);

export const getConsignmentOrders = async (
	page = 1,
	limit = 15,
	columnsFilters = '[]',
	search = '',
	status: string | number = '',
	globalFilters = '[]',
	sort = '',
): Promise<{
	items: ConsignmentOrder[];
	meta: ResponseMetaData;
}> => {
	const response = await http.get<ConsignmentOrdersResponse>(
		'/orders/consignments',
		{
			params: {
				page,
				limit,
				columnsFilters,
				search,
				status,
				globalFilters,
				sort,
			},
		},
	);
	return {
		items: response.data.data.items,
		meta: {
			...response.data.data.metaData,
			itemsPerPage: response.data.data.metaData.recordsPerPage,
		},
	};
};

export const getConsignmentProducts = async (
	page = 1,
	limit = 15,
	globalFilters = '[]',
): Promise<{
	items: ProductSku[];
	meta: ResponseMetaData;
}> => {
	const response = await http.get('/orders/consignments/products', {
		params: {
			page,
			limit,
			globalFilters,
		},
	});
	return {
		items: response.data.data.items,
		meta: {
			...response.data.data.metaData,
			itemsPerPage: response.data.data.metaData.recordsPerPage,
		},
	};
};

/**
 * Create bulk consignment order - sends ONE request to create multiple consignment orders
 * Automatically adds ta_consignmenttype: 953870001 (Trunk Stock) to the payload
 * @param payload - Bulk order payload with products array
 */
export const createBulkConsignmentOrder = async (
	payload: BulkOrderRequest & { ta_consignmenttype: number },
): Promise<any> => {
	const response = await http.post('/orders/create-bulk-request', payload);
	return response.data;
};

/**
 * Update multiple consignment orders in a bulk order group (only quantities and comments)
 * @param payload - Updated bulk order data with products array (orderId + quantity) and shared comments
 */
export const updateBulkConsignmentOrder = async (payload: {
	ta_comments?: string;
	products: Array<{ orderId: string; ta_quantity: string }>;
}): Promise<any> => {
	const response = await http.patch('/orders/update-bulk-request', payload);
	return response.data;
};

/**
 * Get single consignment order details by ID from database
 * @param orderId - The consignment order ID
 */
export const getConsignmentOrderById = async (
	orderId: string,
): Promise<any> => {
	const response = await http.get(`/orders/db/${orderId}`);
	return response.data;
};

/**
 * Get all consignment orders that belong to the same bulk order
 * Bulk consignment orders share the same clinic, doctor, PO, date, etc. but have different products
 * @param orderId - Any order ID from the bulk consignment order group
 */
export const getBulkConsignmentOrders = async (
	orderId: string,
): Promise<any> => {
	const response = await http.get(`/orders/bulk-group/${orderId}`);
	return response.data;
};

/**
 * Get consignment order with shipment details (tracking information)
 * @param orderId - The consignment order ID
 */
export const getConsignmentOrderShipments = async (
	orderId: string,
): Promise<any> => {
	const response = await http.get(`/orders/db/${orderId}?view=details`);
	return response.data;
};
